import { isInterrupt, isLethal, isPowerless, isRescue, type CardDef } from '../../../../core/card';
import { cardDescription } from '../../../../core/cardText';
import { LIGHT, SIGNAL } from '../../../palette';
import { plateCode } from '../codes';
import { box, label, line, solid, type FaceSpec, type Prim } from '../spec';
import { pt } from '../geom';
import { cjkTitle, latinTitle } from './util';
import { t } from '../../../../i18n';

/**
 * **兜底画法。**
 *
 * 它的职责有两个（和原来的 `plain.ts` 一样）：
 *
 * 1. **保证任何一张牌都有画面。** 加新牌忘了配画法时，出现的是一张明显
 *    写着特征的牌，而不是一片空白 —— 空白会让人以为是渲染坏了，查半天。
 * 2. **当通用层的对照组。** 它故意画得平、不加任何特殊效果，所以一张牌
 *    看起来歪了的时候，能立刻判断是透视算错了，而不是"这张牌本来就长这样"。
 *
 * ⚠️ 现在 13 张原版牌**全部**有画面了，所以它只服务一件事：
 * **扩展包塞进来的、还没配画法的新牌**。这就是它的价值 —— 加新牌忘了配画法时，
 * 出现的是一张明显写着特征的牌，而不是一片空白。
 */
export function placeholderFace(card: CardDef): FaceSpec {
  const accent = accentFor(card);
  const prims: Prim[] = [
    box(0, 0, 100, 7, solid(accent)),
    label(plateCode(card.local), 6, 17, { size: 4.2, color: accent, weight: 700, tracking: 0.9 }),
    label(traitLabel(card), 94, 17, {
      size: 4.1,
      color: accent,
      role: 'cjk',
      anchor: 'end',
      tracking: 0.9,
    }),
    line([pt(-12, 20.5), pt(112, 20.5)], { width: 0.5, color: accent, alpha: 0.6 }),

    box(0, 94, 100, 46, solid(0x000000, 0.8)),
    label(cjkTitle(card), 8, 108, {
      size: 13,
      color: LIGHT.base,
      role: 'cjk',
      weight: 900,
      tracking: 1.2,
    }),
    label(latinTitle(card), 8, 113.5, { size: 3.4, color: accent, tracking: 2.4 }),
    line([pt(0, 117.5), pt(100, 117.5)], { width: 0.45, color: accent, alpha: 0.5 }),
    label(cardDescription(card), 8, 124.5, {
      size: 3.5,
      color: LIGHT.base,
      role: 'cjk',
      wrap: 25,
      lineHeight: 4.9,
    }),
    box(0, 135, 100, 5, solid(accent)),
  ];

  return {
    ground: solid(0x000000),
    accent,
    // ⚠️ **不画描边。** 黑底牌和黑桌面之间的边界不靠一圈白线来标 ——
    //    那是最典型的"模板卡"画法，一排手牌会连成一串白框。
    //    边界交给特效去强调：**负形**（桌面在牌后面被"挖空"）比描边高级得多，
    //    而且挖洞是动态的，牌一倾斜就跟着变形，描边只会露馅。
    //    ⚠️ 例外是白卡（异常）—— 它是白的，不描边就糊进背景里了。
    prims,
    // 兜底牌走完整印刷层 —— 它是"普通牌"的样子
    press: true,
  };
}

/**
 * 按**行为特征**给辨识色。刻意不看牌名 —— 扩展包塞进来的新牌自动有颜色。
 *
 * ⚠️ 分派顺序必须和 `docs/card-faces.md` §3 的原型表一致：
 * lethal → rescue → interrupt → powerless → 功能。
 *
 * ⚠️ 打断给的是 `info`（酸青）。第一版给的是 `special`（紫），那是错的 ——
 * 紫是**连击状态**的颜色，状态不是牌。同一张牌在连击里和在手里不该换色。
 */
export function accentFor(card: CardDef): number {
  if (isLethal(card)) return SIGNAL.danger;
  if (isRescue(card)) return SIGNAL.ok;
  if (isInterrupt(card)) return SIGNAL.info;
  // 无效牌不给语义色 —— 它本来就"没效果"
  if (isPowerless(card)) return LIGHT.dim;
  return SIGNAL.neutral;
}

function traitLabel(card: CardDef): string {
  if (isLethal(card)) return t('render.category.lethal');
  if (isRescue(card)) return t('render.category.rescue');
  if (isInterrupt(card)) return t('render.category.interrupt');
  if (isPowerless(card)) return t('render.category.powerless');
  if (card.traits.needsTarget === true) return t('render.category.needsTarget');
  return t('render.category.function');
}
